import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";

/**
 * Flag formulas that include a fully-qualified reference back to the
 * same sheet they live on, e.g. on the "Revenue" worksheet:
 *     =Revenue!A1            → flag
 *     ='Revenue'!A1          → flag
 *     =SUM(Revenue!A:A)      → flag
 *     ='Revenue Quarterly'!  → flag (when sheet is exactly "Revenue Quarterly")
 *
 * Self-sheet references are an Excel anti-pattern: they're redundant,
 * harder to read, break when the sheet is renamed, and trip up
 * formula-copy operations. The unqualified form (=A1) is preferred.
 */

function escapeForRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Check whether `formula` contains a reference to `sheetName`.
 *
 * Handles both forms:
 *   - Unquoted: `SheetName!`  (only legal when the name has no spaces /
 *     punctuation; we match defensively by requiring the previous char
 *     not to be alphanumeric / underscore)
 *   - Quoted:   `'SheetName'!`  (any name; embedded single quotes are
 *     doubled per Excel's convention)
 *
 * Exported for unit testing; production callers should use the audit
 * check below.
 */
export function formulaReferencesSheet(formula: string, sheetName: string): boolean {
  const escapedName = escapeForRegex(sheetName);
  // Quoted variant: in formulas, a single quote inside a quoted sheet
  // name is doubled. So "Joe's" appears as "'Joe''s'!" in formulas.
  const quotedFormName = sheetName.replace(/'/g, "''");
  const escapedQuotedName = escapeForRegex(quotedFormName);

  // Unquoted: previous char must not be part of a longer identifier
  // (avoid matching e.g. "MyRevenue!" when sheet is "Revenue").
  const unquoted = new RegExp(`(?:^|[^A-Za-z0-9_])${escapedName}!`);
  // Quoted: the apostrophe before the name guarantees no identifier
  // ambiguity, but we still need to make sure we're at the start of a
  // quoted reference and not inside an already-quoted string.
  const quoted = new RegExp(`(?:^|[^A-Za-z0-9_'])'${escapedQuotedName}'!`);

  return unquoted.test(formula) || quoted.test(formula);
}


export const selfSheetReference: AuditRule = {
  id: "system.audit.self-sheet-reference",
  name: "Self-sheet reference",
  description:
    "Formulas that reference their own sheet by name (e.g. =Revenue!A1 on the Revenue sheet). " +
    "The qualified form is redundant, makes the formula harder to read, breaks when the sheet is renamed, " +
    "and disrupts formula-copy operations. Prefer the bare reference (=A1).",
  enabled: true,
  source: "system",
  severity: "info",
  /**
   * Uses the workbook-wide R1C1 interning: each distinct formula
   * pattern is checked against the sheet name exactly once via the
   * per-pattern verdict cache. At 1M cells with ~5k distinct patterns,
   * this is ~5k regex evaluations instead of 1M.
   *
   * The regex inside `formulaReferencesSheet` keys off `SheetName!`,
   * which appears identically in A1 and R1C1 form, so the same
   * predicate works on interned R1C1 strings without modification.
   */
  check(sheet, model) {
    const findings: AuditFindingPartial[] = [];
    const sheetName = sheet.name;
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const r1c1Strings = model.catalogs.r1c1Strings;
    const r1c1Ids = sheet.r1c1Ids;

    // Per-pattern verdict cache, scoped to this sheet because the
    // predicate depends on sheetName.
    const flagged = new Set<number>();
    const cleared = new Set<number>();

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const id = r1c1Ids[rowBase + c];
        if (id < 0) continue; // not a formula
        if (cleared.has(id)) continue;
        if (!flagged.has(id)) {
          if (formulaReferencesSheet(r1c1Strings[id], sheetName)) {
            flagged.add(id);
          } else {
            cleared.add(id);
            continue;
          }
        }
        findings.push({
          sheetName,
          address: addressFromRC(startRow + r, startCol + c),
          message: `formula references its own sheet (${sheetName}!) — can be simplified to the bare cell address`,
        });
      }
    }
    return findings;
  },
};
